import type { PersonPaabudDTO } from "@/api/person/paabud";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";
import { formatLessons } from "@/util/number";

import {
  mapIndberetningStatusBadgeVariant,
  mapIndberetningStatusLabel,
} from "./paabudUtils";
import { StatusBadge } from "./StatusBadge";
import { formatDate } from "./utils/formatters";
import { formaterFrist, formaterIndfrielsesperiode } from "./injunctionPeriode";

interface PaabudTableProps {
  paabudList: PersonPaabudDTO[];
  isLoading: boolean;
  error: Error | null;
  onSelectPaabud: (paabudId: string) => void;
}

export const PaabudTable = ({
  paabudList,
  isLoading,
  error,
  onSelectPaabud,
}: PaabudTableProps) => (
  <TableRoot>
    <TableHead>
      <th>Infrielsesperiode</th>
      <th>Påkrævede lektioner</th>
      <th>Lektioner gennemført</th>
      <th>Overførte lektioner</th>
      <th>Status</th>
      <th>Frist</th>

      <th className="text-end">Handling</th>
    </TableHead>
    <TableBody>
      {isLoading && (
        <tr>
          <td colSpan={7} className="text-muted">
            Henter påbud ...
          </td>
        </tr>
      )}
      {error && (
        <tr>
          <td colSpan={7} className="text-danger">
            Kunne ikke hente påbud.
          </td>
        </tr>
      )}
      {!isLoading &&
        !error &&
        paabudList.map((paabud) => {
          const title =
            paabud.decisionDocumentFileName ??
            `${formatLessons(paabud.paakraevedeLektioner)} lektioner`;
          const indberetningStatus =
            paabud.indberetning?.efteruddannelseIndberetningStatus;
          const statusLabel = mapIndberetningStatusLabel(indberetningStatus);

          return (
            <tr
              key={paabud.id}
              aria-label={`Vis påbud ${title}`}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onSelectPaabud(paabud.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectPaabud(paabud.id);
                }
              }}
            >
              <td>
                {formaterIndfrielsesperiode(
                  paabud.periodeFra,
                  paabud.periodeTil,
                  formatDate,
                )}
              </td>

              <td>{paabud.paakraevedeLektioner}</td>
              <td>{formatLessons(paabud.lektionerGennemfoert)}</td>
              <td>{formatLessons(paabud.lektionerOverfoert)}</td>
              <td>
                {indberetningStatus ? (
                  <StatusBadge
                    status={statusLabel}
                    variant={mapIndberetningStatusBadgeVariant(
                      indberetningStatus,
                    )}
                  />
                ) : (
                  "-"
                )}
              </td>
              <td>
                {formaterFrist(
                  paabud.indberetning?.indberetningFrist,
                  formatDate,
                )}
              </td>

              <td className="text-end">
                <button
                  className="btn btn--action btn--sm"
                  id={`efteruddannelse-paabud-vis-${paabud.id}`}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectPaabud(paabud.id);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          );
        })}
      {!isLoading && !error && !paabudList.length && (
        <tr>
          <td colSpan={7} className="text-muted">
            Ingen påbud
          </td>
        </tr>
      )}
    </TableBody>
  </TableRoot>
);
